import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Modal from "src/components/ui/Modal";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import Button from "src/components/ui/Button";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supportRequestActions from "src/redux/slices/supportRequestSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";

interface SupportTicketCreateModalProps {
  open: boolean;
  onClose: () => void;
}

const SupportTicketCreateModal: React.FC<SupportTicketCreateModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { createAdmin } = useAppSelector((state) => state.supportRequest);
  const { tenantList } = useAppSelector((state) => state.tenant);

  const [tenantId, setTenantId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(tenantActions.getTenantsListRequest({ page: 1, limit: 200 }));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (createAdmin.data) {
      toast.success(
        tenantId
          ? "Ticket created — the tenant has been notified."
          : "Internal ticket created."
      );
      dispatch(supportRequestActions.resetRaiseSupportRequestAdmin());
      reset();
      onClose();
    } else if (createAdmin.error) {
      toast.error(`Error: ${createAdmin.error}`);
      dispatch(supportRequestActions.resetRaiseSupportRequestAdmin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAdmin]);

  const tenantOptions = useMemo(
    () =>
      (tenantList?.data?.data || []).map((t: any) => ({
        label: t.tenantName,
        value: t._id,
      })),
    [tenantList?.data?.data]
  );

  const reset = () => {
    setTenantId("");
    setSubject("");
    setMessage("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    dispatch(
      supportRequestActions.raiseSupportRequestAdminRequest({
        ...(tenantId ? { tenantId } : {}),
        subject: subject.trim() || undefined,
        message: message.trim(),
      })
    );
  };

  return (
    <Modal open={open} onClose={handleClose} title="Raise a Ticket" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FilterSelect
          variant="boxed"
          label="Tenant"
          value={tenantId}
          onChange={setTenantId}
          allLabel="No tenant — internal ticket"
          options={tenantOptions}
        />
        <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
          Pick a tenant to notify them and let them reply from their own
          Support page. Leave it blank to track this purely internally.
        </p>

        <Input
          label="Subject"
          placeholder="What's this about?"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          optional
        />

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Message
          </label>
          <textarea
            className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            rows={4}
            placeholder="Describe the issue…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={createAdmin.loading}
            disabled={!message.trim() || createAdmin.loading}
          >
            Create Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SupportTicketCreateModal;
